import React, { useState, useCallback } from 'react'
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Stack,
  Collapse,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import capitalize from 'helpers/node/capitalize'
import ProgramsAutocomplete from 'components/programs/ProgramsAutocomplete'
import MetadataAutocomplete from './MetadataAutocomplete'
import { MODULE_NAMES } from 'utils/constants'
import useConfiguration from 'hooks/useConfiguration'

const JsonEditor = ({ value, onChange, label, disabled, config, errors }) => {
  const [expandedKeys, setExpandedKeys] = useState(new Set())
  const { getMetadataDisplay } = useConfiguration()

  const toggleExpand = useCallback((key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const getValueType = useCallback((value) => {
    if (value === null) return 'null'
    if (Array.isArray(value)) return 'array'
    if (typeof value === 'object') return 'object'
    return typeof value
  }, [])

  const handleAddKey = useCallback(
    (parentPath = '') => {
      const newKey = `newKey${Object.keys(value || {}).length + 1}`
      const newValue = ''

      const newObj = { ...value }
      if (parentPath) {
        const parent = parentPath
          .split('.')
          .reduce((obj, key) => obj[key], newObj)
        parent[newKey] = newValue
      } else {
        newObj[newKey] = newValue
      }
      onChange(newObj)
    },
    [onChange, value]
  )

  const handleDeleteKey = useCallback(
    (path) => {
      const keys = path.split('.')
      const lastKey = keys.pop()

      const newObj = { ...value }
      const parent = keys.length
        ? keys.reduce((obj, key) => obj[key], newObj)
        : newObj
      delete parent[lastKey]
      onChange(newObj)
    },
    [onChange, value]
  )

  const handleKeyChange = useCallback(
    (oldPath, newKey) => {
      const keys = oldPath.split('.')
      const lastKey = keys.pop()
      const parentPath = keys.join('.')

      const newObj = { ...value }
      const parent = parentPath
        ? parentPath.split('.').reduce((obj, key) => obj[key], newObj)
        : newObj
      const val = parent[lastKey]
      delete parent[lastKey]
      parent[newKey] = val
      onChange(newObj)
    },
    [onChange, value]
  )

  const handleValueChange = useCallback(
    (path, newValue, type) => {
      let parsedValue = newValue
      switch (type) {
        case 'number':
          parsedValue = Number(newValue)
          break
        case 'boolean':
          parsedValue = newValue === 'true'
          break
        case 'null':
          parsedValue = null
          break
        case 'object':
          parsedValue = {}
          break
        case 'array':
          parsedValue = []
          break
        default:
          parsedValue = newValue
      }

      const newObj = { ...value }
      const keys = path.split('.')
      const lastKey = keys.pop()
      const parent = keys.length
        ? keys.reduce((obj, key) => obj[key], newObj)
        : newObj
      parent[lastKey] = parsedValue
      onChange(newObj)
    },
    [onChange, value]
  )

  const renderValue = useCallback(
    (_value, path, depth = 0) => {
      const type = getValueType(_value)
      const isExpanded = expandedKeys.has(path)
      const isObjectOrArray = type === 'object' || type === 'array'
      let isReadOnly = disabled || type === 'null'
      let isDisabled = disabled || type === 'null'

      const renderValueInput = () => {
        if (path === 'path' && config?.ruleDetail === 'program_id') {
          _value = 'Program ID'
          isReadOnly = true
        }

        if (path === 'mode') {
          isDisabled = true
        }

        if (path === 'value' && config?.ruleDetail === 'program_id') {
          return (
            <ProgramsAutocomplete
              multiple={false}
              selectedPrograms={_value}
              setSelectedPrograms={(program) =>
                handleValueChange(path, program.id, type)
              }
              disabled={isDisabled}
              size="small"
              sx={{ flex: 1 }}
              textFieldProps={{
                error: Boolean(errors?.rule?.[path]),
                placeholder: errors?.rule?.[path],
              }}
            />
          )
        }

        if (path === 'path' && config?.ruleDetail === 'metadata') {
          return (
            <MetadataAutocomplete
              value={_value}
              setSelectedOption={(option) =>
                handleValueChange(path, option?.path || '', type)
              }
              moduleName={MODULE_NAMES.ADDRESS_OVERRIDE}
              size="small"
              sx={{ flex: 1 }}
            />
          )
        }

        return (
          <TextField
            size="small"
            value={_value === null ? '' : String(_value)}
            onChange={(e) => handleValueChange(path, e.target.value, type)}
            disabled={isDisabled}
            sx={{ flex: 1 }}
            inputProps={{
              readOnly: isReadOnly,
            }}
            error={Boolean(errors?.rule?.[path])}
            helperText={errors?.rule?.[path]}
          />
        )
      }

      return (
        <Box sx={{ pl: depth * 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {isObjectOrArray && (
              <IconButton size="small" onClick={() => toggleExpand(path)}>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
            <TextField
              size="small"
              value={capitalize(path.split('.').pop())}
              onChange={(e) => handleKeyChange(path, e.target.value)}
              disabled={isDisabled}
              inputProps={{
                readOnly: true,
              }}
              sx={{ width: 200 }}
            />
            {!isObjectOrArray && renderValueInput()}
            {false && (
              <IconButton
                size="small"
                onClick={() => handleDeleteKey(path)}
                disabled={isDisabled}
              >
                <DeleteIcon />
              </IconButton>
            )}
            {false && isObjectOrArray && (
              <IconButton
                size="small"
                onClick={() => handleAddKey(path)}
                disabled={isDisabled}
              >
                <AddIcon />
              </IconButton>
            )}
          </Stack>
          {false && isObjectOrArray && isExpanded && (
            <Collapse in={isExpanded}>
              <Box sx={{ pl: 2 }}>
                {Object.entries(_value || {}).map(([key, val]) => (
                  <Box key={key}>
                    {renderValue(val, `${path}.${key}`, depth + 1)}
                  </Box>
                ))}
                <Box sx={{ pl: 2, mt: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleAddKey(path)}
                    disabled={isDisabled}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              </Box>
            </Collapse>
          )}
        </Box>
      )
    },
    [
      expandedKeys,
      getValueType,
      handleAddKey,
      handleDeleteKey,
      handleKeyChange,
      handleValueChange,
      toggleExpand,
      disabled,
      config,
      getMetadataDisplay,
    ]
  )

  return (
    <Box>
      {label && (
        <Typography variant="h2" sx={{ mb: 2 }}>
          {label}
        </Typography>
      )}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Stack spacing={1}>
          {Object.entries(value || {}).map(([key, val]) => (
            <Box key={key}>{renderValue(val, key)}</Box>
          ))}
        </Stack>
        {false && (
          <Box sx={{ mt: 1 }}>
            <IconButton
              size="small"
              onClick={() => handleAddKey()}
              disabled={disabled}
            >
              <AddIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default JsonEditor
